/**
 * v-platform-expose 指令（注册名 platform-expose）
 * 使用项目专属名，避免与第三方组件库全局注册的 expose 指令冲突
 * 当绑定元素出现在视口中时，触发指定的回调函数
 *
 * 参数说明：
 *   handler    {Function} 元素进入视口时的回调，参数为 (entry, data)
 *   once       {Boolean}  是否只触发一次，默认 true
 *   validTime  {Number}   元素持续可见多少毫秒后才触发回调，默认 100ms
 *                         用于过滤快速滑动时一闪而过的元素，传 0 则立即触发
 *   data       {Any}      透传给 handler 的自定义数据，handler 第二个参数接收
 *
 * 调用示例：
 *
 * 1. 直接传函数（once=true，validTime=100）
 *    v-platform-expose="handleExpose"
 *
 * 2. 传配置对象，携带自定义数据
 *    v-platform-expose="{ handler: handleExpose, data: item }"
 *
 * 3. 完整配置
 *    v-platform-expose="{ handler: handleExpose, once: false, validTime: 200, data: item }"
 *
 * 4. 不需要防抖，立即触发
 *    v-platform-expose="{ handler: handleExpose, validTime: 0 }"
 *
 * 5. 外部访问单例 observer
 *    import { sharedObserver } from '@/directives/expose'
 *    sharedObserver.observe(someEl)
 */

// WeakMap 存储每个元素对应的回调配置及定时器
const callbackMap = new WeakMap();

// 单例 IntersectionObserver，threshold=0 表示元素刚出现即触发
export const sharedObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    const config = callbackMap.get(entry.target);
    if (!config) return;

    if (entry.isIntersecting) {
      if (config.validTime > 0) {
        // 延迟后二次确认元素是否仍在视口内，防止页面加载时被后续内容挤出视口
        config.timer = setTimeout(() => {
          config.timer = null;
          config.handler(entry, config.data);
          if (config.once) {
            sharedObserver.unobserve(entry.target);
            callbackMap.delete(entry.target);
          }
        }, config.validTime);
      } else {
        // validTime 为 0，直接触发
        config.handler(entry, config.data);
        if (config.once) {
          sharedObserver.unobserve(entry.target);
          callbackMap.delete(entry.target);
        }
      }
    } else if (config.timer) {
      // 元素离开视口，取消尚未触发的定时器（过滤快速滑动）
      clearTimeout(config.timer);
      config.timer = null;
    }
  });
}, { threshold: 1 });

/**
 * 解析指令绑定值
 */
function parseBinding(binding) {
  if (typeof binding.value === 'function') {
    return {
      handler: binding.value, once: true, validTime: 100, data: undefined,
    };
  }
  const config = binding.value || {};
  const once = config.once !== undefined ? config.once : true;
  const validTime = config.validTime !== undefined ? config.validTime : 100;
  return {
    handler: config.handler, once, validTime, data: config.data,
  };
}

export default {
  inserted(el, binding) {
    const {
      handler, once, validTime, data,
    } = parseBinding(binding);

    if (typeof handler !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[v-expose] 绑定值必须是函数或包含 handler 函数的对象');
      return;
    }

    callbackMap.set(el, {
      handler, once, validTime, data, timer: null,
    });
    sharedObserver.observe(el);
  },

  update(el, binding) {
    if (binding.value === binding.oldValue) return;

    const {
      handler, once, validTime, data,
    } = parseBinding(binding);
    if (typeof handler !== 'function') return;

    // 元素已被 once 触发后从 callbackMap 中移除，不再重新注册
    const old = callbackMap.get(el);
    if (!old) return;

    // 清理旧的定时器和监听
    if (old.timer) clearTimeout(old.timer);
    sharedObserver.unobserve(el);

    // 重新注册
    callbackMap.set(el, {
      handler, once, validTime, data, timer: null,
    });
    sharedObserver.observe(el);
  },

  unbind(el) {
    const config = callbackMap.get(el);
    if (config && config.timer) clearTimeout(config.timer);
    sharedObserver.unobserve(el);
    callbackMap.delete(el);
  },
};
